<template>
  <a
    v-if="game && !game.geoRestrict && !isGameBlockedByBonus"
    :class="[$style.gameThumbnail, $style[variant]]"
    :href="gameFullPath"
    @click.prevent="gameOpen(gameCurrency)"
  >
    <div :class="[$style.gameThumbnailImgHolder, { [$style.isLoading]: !isGameLoaded }]">
      <picture>
        <!-- Mobile: <= 576px -->
        <source
          media="(max-width: 576px)"
          :srcset="`${gameImg}?w=200&q=70&fit=cover&format=webp`"
        />
        <!-- Mobile+: > 576px -->
        <source
          media="(min-width: 577px)"
          :srcset="`${gameImg}?w=200&q=70&fit=cover&format=webp`"
        />

        <img
          :src="`${gameImg}?w=200&q=70&fit=cover&format=webp`"
          loading="lazy"
          :class="$style.gameThumbnailImg"
          :alt="game.title"
        />
      </picture>
    </div>

    <BitThumbLabel
      v-if="gameLabel && variant !== 'freespins'"
      :class="$style.gameThumbnailLabel"
      :label="gameLabel"
    />

    <div :class="$style.gameThumbnailPlayIcon">
      <img
        src="/images/common/ico_play_blue.svg?url_encode"
        loading="lazy"
        fetchpriority="low"
        :class="$style.gameThumbnailPlay"
        height="56"
        alt=""
        width="56"
      />
    </div>
    <BitFavoriteIconButton
      v-if="isPlayerAuthorised && variant !== 'freespins'"
      :class="$style.gameThumbnailFavoriteBtn"
      :is-favourite="favouriteData.isFavourite"
      @click.prevent.stop="favouritesListToggle()"
    />
  </a>
  <div
    v-else-if="(game && game.geoRestrict) || (game && isGameBlockedByBonus)"
    :class="[$style.gameThumbnail, $style[variant], $style.restricted]"
  >
    <div :class="[$style.gameThumbnailImgHolder, { [$style.isLoading]: !isGameLoaded }]">
      <picture>
        <!-- Mobile: <= 576px -->
        <source
          media="(max-width: 576px)"
          :srcset="`${gameImg}?w=125&q=60&fit=cover&format=webp`"
        />
        <!-- Mobile+: > 576px -->
        <source
          media="(min-width: 577px)"
          :srcset="`${gameImg}?w=170&q=60&fit=cover&format=webp`"
        />

        <img
          :src="`${gameImg}?w=170&q=60&fit=cover&format=webp`"
          loading="lazy"
          :class="$style.gameThumbnailImg"
          :alt="game.title"
        />
      </picture>
    </div>
    <img
      v-if="gameProviderIcon.url"
      loading="lazy"
      :class="$style.gameThumbnailProviderIcon"
      :height="gameProviderIcon.height"
      :width="gameProviderIcon.width"
      :src="gameProviderIcon.url"
      :alt="game.provider"
    />

    <BitThumbLabel
      v-if="gameLabel && variant !== 'freespins'"
      :class="$style.gameThumbnailLabel"
      :label="gameLabel"
    />

    <div :class="$style.gameRestrictedText">
      <span v-if="game.geoRestrict">{{ gameNotAvailableTitle }}</span>
      <span v-else>{{ gameBlockedByBonusTitle }}</span>
    </div>
    <BitFavoriteIconButton
      v-if="isPlayerAuthorised && variant !== 'freespins'"
      :class="$style.gameThumbnailFavoriteBtn"
      :is-favourite="favouriteData.isFavourite"
      @click.prevent.stop="favouritesListToggle()"
    />
  </div>
</template>

<script lang="ts" setup>
import { BitFavoriteIconButton, BitThumbLabel } from '@bit/ui-kit';
import { useUrlConstructor } from '~/composables/useUrlConstructor';
import { useStoreGtm } from '~/stores/abstract/gtm';
import { useStoreModals } from '~/stores/abstract/modals';
import { useStoreRoot } from '~/stores/abstract/root';
import { useStoreSsPlayer } from '~/stores/softswiss/player';
import { useStoreSsPlayerBonuses } from '~/stores/softswiss/playerBonuses';
import { useStoreCataloguePage } from '~/stores/strapi/cataloguePage';
import { useStoreCategoryPage } from '~/stores/strapi/categoryPage';
import { useStoreComponents } from '~/stores/strapi/components';
import { useStoreProviders } from '~/stores/strapi/providers';
import type { EModalName } from '~/types/modals';
import type { ISsGame } from '~/types/softswiss/ssGames';

const props = defineProps({
  game: {
    type: Object as PropType<ISsGame>,
    required: true,
  },
  gtmAction: {
    type: String,
    default: '',
  },
  /* when game is opened, modal passed by this prop will be closed  */
  closeActiveModalOnOpenGame: {
    type: [String, undefined] as PropType<EModalName | undefined>,
    required: false,
    default: undefined,
  },
  /* if this prop is passed , when user opens the game (not in demo)
        the currency will be set to this value, default is currently selected currency  */
  gameCurrency: {
    type: [String, undefined] as PropType<string | undefined>,
    required: false,
    default: undefined,
  },
  favoriteGames: {
    type: Array as PropType<number[]>,
    required: false,
  },
  imgSize: {
    type: String as PropType<'s4' | 's6'>,
    default: 's6',
  },
  isGameShouldOpenByEmit: {
    type: Boolean,
    default: false,
  },
  variant: {
    type: String as PropType<'default' | 'freespins'>,
    default: 'default',
  },
  collectionId: {
    type: String,
    default: '',
  },
});
const emit = defineEmits<{
  (e: 'remove-from-favorite', value: number): void;
  (e: 'add-to-favorite', value: number): void;
  (e: 'openGame', value: ISsGame): void;
}>();

const storeModals = useStoreModals();
const storeSsPlayer = useStoreSsPlayer();
const storeRoot = useStoreRoot();
const providerStore = useStoreProviders();
const storeComponents = useStoreComponents();
const storeCategoryPage = useStoreCategoryPage();
const storeCataloguePage = useStoreCataloguePage();
const playerSsBonuses = useStoreSsPlayerBonuses();
const { getGameUrl } = useUrlConstructor();

const gameId = computed(
  () => props.game.real?.FUN?.id || props.game.real?.BTC?.id || props.game.real?.USD?.id
);

const favouriteData = computed(() => {
  if (!props.favoriteGames) {
    return {
      isFavourite: false,
      gameId: null,
    };
  }
  const favouriteGameId = props.favoriteGames.find(
    (id) =>
      id === props.game.real?.FUN?.id ||
      id === props.game.real?.BTC?.id ||
      id === props.game.real?.USD?.id
  );
  return {
    isFavourite: Boolean(favouriteGameId),
    gameId: favouriteGameId,
  };
});

const gameImg = computed(
  () => `/image-cdn/ss/i/${props.imgSize}/${props.game.externalId || props.game.id}.webp`
);

const isGameLoaded = computed(() => props.game.externalId || props.game.id);

const gameProviderIcon = computed(() => providerStore.iconsMap[props.game.provider] || { url: '' });

const gameFullPath = computed(() =>
  getGameUrl({ id: props.game.externalId, provider: props.game.provider, title: props.game.title })
);

const isPlayerAuthorised = computed(() => storeSsPlayer.isPlayerAuthorised);

const gameNotAvailableTitle = computed(() => storeComponents.search.gameNotAvailableTitle);
const gameBlockedByBonusTitle = computed(() => storeComponents.search.gameBlockedByBonusTitle);

const isGameBlockedByBonus = computed(() => {
  const activeWagerBonusesCurrency = playerSsBonuses.activeBonusesAndFreespins
    .filter((bonus) => bonus.stage === 'handle_bets')
    .map((bonus) => bonus.currency);

  if (
    !activeWagerBonusesCurrency.length ||
    !activeWagerBonusesCurrency.includes(storeRoot.userCurrency)
  ) {
    return false;
  }

  const gameCollectionsKeys = Object.keys(props.game.collections || {});

  return gameCollectionsKeys.some((c) => c.includes('jackpot') || c.startsWith('jackpot'));
});

const favouritesListToggle = (): void => {
  if (props.game) {
    if (favouriteData.value.isFavourite) {
      emit('remove-from-favorite', favouriteData.value.gameId as number);
    } else {
      emit('add-to-favorite', gameId.value);
    }
  }
};

const gameLabel = computed(() => {
  const collectionsList = Object.keys(props.game.collections);

  const priority: Record<string, number> = {
    hot: 1,
    _exclusive: 2,
    new: 3,
    hits: 4,
  };

  const isGameInHotCollection = collectionsList.some((key) => {
    return key === 'hot' || key.startsWith(`hot:`);
  });
  const isGameInNewCollection = collectionsList.some((key) => {
    return key === 'new' || key.startsWith(`new:`);
  });
  const isGameInExclusiveCollection = collectionsList.some((key) => {
    return key === '_exclusive' || key.startsWith(`_exclusive:`);
  });
  const isGameInHitsCollection = collectionsList.some((key) => {
    return key === 'hits' || key.startsWith(`hits:`);
  });

  const label = [...storeCategoryPage.activeLabels]
    .sort((a, b) => {
      const pa = priority[a.categoryId] ?? 999;
      const pb = priority[b.categoryId] ?? 999;
      return pa - pb;
    })
    .find((l) => {
      if (props.collectionId === l.categoryId) {
        return false;
      }
      if (props.collectionId === '7bit-exclusive' && l.categoryId === '_exclusive') {
        return false;
      }

      if (l.categoryId === 'hot' && isGameInHotCollection) {
        return true;
      }
      if (
        l.categoryId === '_exclusive' &&
        isGameInExclusiveCollection &&
        props.collectionId !== '7bit-exclusive'
      ) {
        return true;
      }
      if (l.categoryId === 'new' && isGameInNewCollection) {
        return true;
      }
      if (l.categoryId === 'hits' && isGameInHitsCollection) {
        return true;
      }
      return collectionsList.some((key) => {
        return key === l.categoryId || key.startsWith(`${l.categoryId}:`);
      });
    });

  if (label?.categoryTitle) {
    return {
      title: label.categoryTitle,
      color: label?.labelType,
    };
  }

  return null;
});

const gameOpen = (currency?: string | undefined) => {
  if (
    storeCataloguePage.selectedCollection &&
    storeCataloguePage.selectedCollection !== 'allGames'
  ) {
    useStoreGtm().push({
      event: 'game_open',
      event_label: storeCataloguePage.selectedCollection,
      game_label: props.game.title,
    });
  }

  if (props.isGameShouldOpenByEmit) {
    emit('openGame', props.game);

    return;
  }

  if (!currency) {
    currency = storeRoot.userCurrency;
  }

  if (props.closeActiveModalOnOpenGame) {
    storeModals.closeModal(props.closeActiveModalOnOpenGame);
  }

  return navigateTo({ path: gameFullPath.value });
};

// watch(
//   () => gameImg,
//   ({ value }) => {
//     if (value) {
//       useHead({
//         link: [
//           {
//             rel: 'preload',
//             href: value,
//             as: 'image',
//             crossorigin: '',
//           },
//         ],
//       });
//     }
//   },
//   { immediate: true }
// );
</script>

<style lang="scss" module src="./gameThumbnail.scss"></style>
